import React, { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import HeroSection from '@/components/HeroSection';
import BadgesSection from '@/components/BadgesSection';
import PortfolioSimulator from '@/components/PortfolioSimulator';
import LuxembourgInsuranceSection from '@/components/LuxembourgInsuranceSection';
import ServicesSection from '@/components/ServicesSection';
import Footer from '@/components/Footer';
import MarketTicker from '@/components/MarketTicker';
import FinancialNewsSection from '@/components/FinancialNewsSection';
import GoldInvestmentSection from '@/components/GoldInvestmentSection';
import StockInvestmentSection from '@/components/StockInvestmentSection';
import CryptoInvestmentSection from '@/components/CryptoInvestmentSection';
import EventsSection from '@/components/EventsSection';
import VideoBackgroundSection from '@/components/VideoBackgroundSection';
import FAQSection from '@/components/FAQSection';
import HydrogenApproachSection from '@/components/HydrogenApproachSection';
import ContactSection from '@/components/ContactSection';
import PageMeta from '@/components/SEO/PageMeta';
import { TooltipWrapper } from "@/components/ui/tooltip";

// FadeInSection component to handle animation
const FadeInSection = ({ children, id }: { children: React.ReactNode, id?: string }) => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = React.useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });
    
    const currentRef = domRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }
    
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);
  
  return (
    <div
      id={id}
      className={`transition-opacity duration-1000 ease-in-out ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      ref={domRef}
    >
      {children}
    </div>
  );
};

const Index = () => {
  return (
    <TooltipWrapper>
      <div className="min-h-screen flex flex-col">
        <PageMeta 
          title="La Patrimoniale - Gestion de Patrimoine Paris, Optimisation Fiscale & Conseil"
          description="La Patrimoniale, cabinet expert en gestion de patrimoine à Paris depuis 1980 : découvrez toutes nos solutions haut de gamme pour optimiser, protéger, transmettre et faire fructifier votre patrimoine. Nos conseillers La Patrimoniale travaillent sur l'optimisation fiscale, l'investissement, l'épargne retraite, l'assurance-vie, l'immobilier et l'accompagnement patrimonial sur mesure pour chaque client. Obtenez un bilan patrimonial, une analyse personnalisée et la meilleure stratégie pour développer votre patrimoine avec La Patrimoniale, référence en gestion de fortune à Paris."
          keywords="La Patrimoniale, gestion de patrimoine Paris, cabinet La Patrimoniale, optimisation fiscale, conseil patrimonial, investissement immobilier, épargne, retraite, bilan patrimonial gratuit, expert gestion patrimoine, stratégie patrimoniale, gestion fortune, cabinet conseil, analyse patrimoniale, patrimoine Paris"
          canonical="https://www.la-patrimoniale-gestion.com/"
        />
        <Navbar />
        <main className="flex-grow">
          {/* Hero section doesn't need fade in */}
          <HeroSection />
          <MarketTicker />
          
          <FadeInSection>
            <BadgesSection />
          </FadeInSection>
          
          {/* Gold section now before services */}
          <FadeInSection id="gold-section">
            <GoldInvestmentSection />
          </FadeInSection>
          
          <FadeInSection>
            <PortfolioSimulator />
          </FadeInSection>
          
          <FadeInSection id="stock-section">
            <StockInvestmentSection />
          </FadeInSection>
          
          <FadeInSection id="services-section">
            <ServicesSection />
          </FadeInSection>
          
          {/* Luxembourg insurance section moved after services section */}
          <FadeInSection id="luxembourg-insurance">
            <LuxembourgInsuranceSection />
          </FadeInSection>
          
          <FadeInSection>
            <FinancialNewsSection />
          </FadeInSection>
          
          <FadeInSection id="crypto-section">
            <CryptoInvestmentSection />
          </FadeInSection>
          
          <FadeInSection>
            <EventsSection />
          </FadeInSection>
          
          <FadeInSection>
            <VideoBackgroundSection />
          </FadeInSection>
          
          <FadeInSection>
            <FAQSection />
          </FadeInSection>
          
          <FadeInSection id="hydrogen-section">
            <HydrogenApproachSection />
          </FadeInSection>
          
          <FadeInSection id="contact-section">
            <ContactSection />
          </FadeInSection>
        </main>
        <Footer />
      </div>
    </TooltipWrapper>
  );
};
export default React.memo(Index);
